import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, formatDistanceStrict, parseISO } from 'date-fns';
import { ArrowLeft, CheckCircle2, Mail, Clock, CalendarDays, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { SidebarLayout } from '@/layouts/SidebarLayout';
import { EmailSidebar } from '@/components/email/EmailSidebar';
import { useHermesCampaign, useHermesCampaignJobs } from '@/hooks/useHermesCampaigns';
import { resolveCampaignTotals, describeCampaignWindow } from '@/lib/email/campaign-totals';
import { computeCampaignJobCounts } from '@/lib/email/job-status';
import { useState } from 'react';

// Adaptive granularity levels (finest → coarsest)
const GRANULARITIES = [15, 30, 60, 120, 240, 1440] as const;
const MAX_BARS = 48;
const MAX_VISIBLE_LABELS = 12;

interface Bucket {
  timestamp: number;
  count: number;
  isNewDay: boolean;
}

const GRANULARITY_LABELS: Record<number, string> = {
  15: '15-minute blocks',
  30: '30-minute blocks',
  60: 'Hourly blocks',
  120: '2-hour blocks',
  240: '4-hour blocks',
  1440: 'Daily',
};

function formatBarLabel(timestamp: number, granularity: number): string {
  const d = new Date(timestamp);
  if (granularity >= 1440) return format(d, 'MMM d');
  if (granularity >= 60) return format(d, 'haaa');
  return format(d, 'h:mmaaa');
}

function formatTooltipHeader(timestamp: number, granularity: number): string {
  const d = new Date(timestamp);
  if (granularity >= 1440) return format(d, 'EEEE, MMM d');
  const end = new Date(timestamp + granularity * 60000);
  return `${format(d, 'MMM d, h:mm a')} – ${format(end, 'h:mm a')}`;
}

export function CampaignSummaryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: campaign, isLoading: campaignLoading } = useHermesCampaign(id);
  const { data: jobs = [], isLoading: jobsLoading, isSuccess: jobsLoaded } = useHermesCampaignJobs(id);
  const [emailActiveTab, setEmailActiveTab] = useState('campaigns');

  const isLoading = campaignLoading || jobsLoading;

  // Durable totals off the campaign row (#859): hermes_email_queue_jobs rows are purged 7
  // days after sending, so jobs.length decays and can no longer be used as the campaign
  // total. The chart below still buckets off the surviving rows — a legitimate
  // recent-activity window, captioned as such further down when it's partial or empty.
  //
  // The window is withheld until the query has actually succeeded; an in-flight or failed
  // query yields the same empty `jobs` as a purge and must not be described as one.
  // `isSuccess` rather than `!isLoading`: react-query v5 reports isLoading=false for a
  // disabled query that has never fetched.
  const windowSettled = jobsLoaded;
  const totals = useMemo(
    () => resolveCampaignTotals(campaign ?? {}, windowSettled ? computeCampaignJobCounts(jobs) : null),
    [campaign, jobs, windowSettled],
  );
  const windowNotice = describeCampaignWindow(totals);

  // Adaptive time-bucketed chart data
  const { buckets, maxCount, firstSend, lastSend, granularityMinutes, isMultiDay } = useMemo(() => {
    const empty = { buckets: [] as Bucket[], maxCount: 0, firstSend: null as string | null, lastSend: null as string | null, granularityMinutes: 1440, isMultiDay: false };
    if (!jobs.length) return empty;

    const scheduledJobs = jobs
      .filter(j => j.scheduled_send_time)
      .sort((a, b) => a.scheduled_send_time!.localeCompare(b.scheduled_send_time!));
    if (!scheduledJobs.length) return empty;

    const first = scheduledJobs[0].scheduled_send_time!;
    const last = scheduledJobs[scheduledJobs.length - 1].scheduled_send_time!;
    const firstTime = parseISO(first);
    const lastTime = parseISO(last);
    const spanMinutes = (lastTime.getTime() - firstTime.getTime()) / 60000;

    // Pick the finest granularity that keeps total bars within MAX_BARS
    let chosen = 1440;
    for (const gran of GRANULARITIES) {
      const slotCount = spanMinutes > 0 ? Math.floor(spanMinutes / gran) + 1 : 1;
      if (slotCount <= MAX_BARS) {
        chosen = gran;
        break;
      }
    }

    // Compute the bucket start timestamp for a given time
    const getBucketStart = (time: Date): number => {
      const dayStart = new Date(time.getFullYear(), time.getMonth(), time.getDate()).getTime();
      if (chosen >= 1440) return dayStart;
      const minutesSinceDayStart = (time.getTime() - dayStart) / 60000;
      const bucketMinutes = Math.floor(minutesSinceDayStart / chosen) * chosen;
      return dayStart + bucketMinutes * 60000;
    };

    // Bucket the jobs
    const bucketMap = new Map<number, number>();
    for (const job of scheduledJobs) {
      const t = parseISO(job.scheduled_send_time!);
      const key = getBucketStart(t);
      bucketMap.set(key, (bucketMap.get(key) || 0) + 1);
    }

    // Fill in empty slots between first and last to show the true timeline
    const firstBucket = getBucketStart(firstTime);
    const lastBucket = getBucketStart(lastTime);
    const granMs = chosen * 60000;

    const result: Bucket[] = [];
    let prevDay = -1;
    for (let t = firstBucket; t <= lastBucket; t += granMs) {
      const d = new Date(t);
      const day = d.getDate() + d.getMonth() * 100; // unique per calendar day
      const isNewDay = day !== prevDay;
      prevDay = day;
      result.push({ timestamp: t, count: bucketMap.get(t) || 0, isNewDay });
    }

    result.sort((a, b) => a.timestamp - b.timestamp);
    const max = Math.max(...result.map(b => b.count));
    const multiDay = result.filter(b => b.isNewDay).length > 1;

    return {
      buckets: result,
      maxCount: max,
      firstSend: first,
      lastSend: last,
      granularityMinutes: chosen,
      isMultiDay: multiDay,
    };
  }, [jobs]);

  // Determine which bar labels to show (avoid crowding)
  const labelStep = Math.max(1, Math.ceil(buckets.length / MAX_VISIBLE_LABELS));
  const shouldShowLabel = (index: number, bucket: Bucket): boolean => {
    if (index === 0 || index === buckets.length - 1) return true;
    if (isMultiDay && bucket.isNewDay) return true;
    return index % labelStep === 0;
  };

  // Show inline count above bars only when there's enough room
  const showInlineCounts = buckets.length <= 24;

  const duration = firstSend && lastSend
    ? formatDistanceStrict(parseISO(lastSend), parseISO(firstSend))
    : '—';

  if (isLoading) {
    return (
      <SidebarLayout sidebar={<EmailSidebar activePage={emailActiveTab} onPageChange={setEmailActiveTab} />}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </SidebarLayout>
    );
  }

  if (!campaign) {
    return (
      <SidebarLayout sidebar={<EmailSidebar activePage={emailActiveTab} onPageChange={setEmailActiveTab} />}>
        <div className="p-8">
          <Button variant="ghost" onClick={() => navigate('/inbox?tab=campaigns')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
          <div className="text-center py-8 text-muted-foreground">Campaign not found</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout sidebar={<EmailSidebar activePage={emailActiveTab} onPageChange={setEmailActiveTab} />}>
      <div className="h-full flex flex-col">
        <div className="flex-1 p-8 space-y-6 overflow-y-auto">
          {/* Success banner */}
          <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
            <div>
              <h1 className="text-xl font-bold text-green-800 dark:text-green-200">Campaign Scheduled!</h1>
              <p className="text-sm text-green-700 dark:text-green-300">{campaign.name}</p>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-2xl font-bold">{totals.recipients}</div>
                  <div className="text-xs text-muted-foreground">Total Emails</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-2xl font-bold">
                    {firstSend ? format(parseISO(firstSend), 'h:mm a') : '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {firstSend ? format(parseISO(firstSend), 'MMM d') : 'First Send'}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <CalendarDays className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-2xl font-bold">
                    {lastSend ? format(parseISO(lastSend), 'MMM d') : '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">Last Send</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Timer className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-2xl font-bold">{duration}</div>
                  <div className="text-xs text-muted-foreground">Duration</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Schedule visualization */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">When your emails will go out</CardTitle>
              {buckets.length > 0 && (
                <CardDescription>{GRANULARITY_LABELS[granularityMinutes]}</CardDescription>
              )}
              {/* #859: caption the window so surviving rows are never mistaken for the whole
                  campaign. describeCampaignWindow stays silent until the query has settled. */}
              {windowNotice && <CardDescription>{windowNotice}</CardDescription>}
            </CardHeader>
            <CardContent className="space-y-4">
              {buckets.length > 0 ? (
                <>
                  {/* Bar chart */}
                  <div className="space-y-1">
                    <div className="flex items-end gap-[3px] h-40">
                      {buckets.map((bucket) => {
                        const heightPct = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
                        return (
                          <div
                            key={bucket.timestamp}
                            className="flex-1 flex flex-col items-center min-w-0 group"
                          >
                            <div className="relative w-full flex justify-center flex-1 items-end">
                              {/* Tooltip */}
                              {bucket.count > 0 && (
                                <div className="absolute bottom-full mb-1 hidden group-hover:block z-10">
                                  <div className="bg-popover text-popover-foreground text-xs rounded-md shadow-md border px-3 py-2 whitespace-nowrap">
                                    <div className="font-medium">
                                      {formatTooltipHeader(bucket.timestamp, granularityMinutes)}
                                    </div>
                                    <div>
                                      {bucket.count} email{bucket.count !== 1 ? 's' : ''}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {/* Bar */}
                              {bucket.count > 0 ? (
                                <div
                                  className="w-full bg-primary/80 hover:bg-primary rounded-t transition-colors cursor-default"
                                  style={{ height: `${Math.max(heightPct, 6)}%` }}
                                />
                              ) : (
                                <div className="w-full bg-muted/30 rounded-t" style={{ height: '2px' }} />
                              )}
                            </div>
                            {/* Inline count */}
                            {showInlineCounts && bucket.count > 0 && (
                              <span className="text-[10px] font-medium leading-none mt-0.5">
                                {bucket.count}
                              </span>
                            )}
                            {showInlineCounts && bucket.count === 0 && (
                              <span className="text-[10px] leading-none mt-0.5 invisible">0</span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Time labels row */}
                    <div className="flex gap-[3px]">
                      {buckets.map((bucket, idx) => (
                        <div key={`label-${bucket.timestamp}`} className="flex-1 min-w-0 text-center">
                          {shouldShowLabel(idx, bucket) ? (
                            <span className="text-[10px] text-muted-foreground leading-tight block truncate">
                              {formatBarLabel(bucket.timestamp, granularityMinutes)}
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </div>

                    {/* Date row for multi-day sub-daily charts */}
                    {isMultiDay && granularityMinutes < 1440 && (
                      <div className="flex gap-[3px] -mt-0.5">
                        {buckets.map((bucket) => (
                          <div key={`date-${bucket.timestamp}`} className="flex-1 min-w-0 text-center">
                            {bucket.isNewDay ? (
                              <span className="text-[10px] font-medium text-foreground leading-tight block truncate">
                                {format(new Date(bucket.timestamp), 'MMM d')}
                              </span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Plain-English summary */}
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {totals.recipients} email{totals.recipients !== 1 ? 's' : ''} will be sent
                    {isMultiDay
                      ? ` over ${buckets.filter(b => b.isNewDay).length} days`
                      : ' today'}
                    .{' '}
                    {firstSend && (
                      <>
                        The first email goes out{' '}
                        {format(parseISO(firstSend), 'EEEE') === format(new Date(), 'EEEE')
                          ? 'today'
                          : `on ${format(parseISO(firstSend), 'EEEE')}`}{' '}
                        at {format(parseISO(firstSend), 'h:mm a')}.{' '}
                      </>
                    )}
                    Each email is spaced 15 minutes apart.{' '}
                    {lastSend && (
                      <>
                        The last email will be sent on{' '}
                        {format(parseISO(lastSend), 'MMMM d')} at{' '}
                        {format(parseISO(lastSend), 'h:mm a')}.
                      </>
                    )}
                  </p>
                </>
              ) : (
                // #859: only promise that jobs are coming when the caption above hasn't
                // already explained why there are none. Revisiting an old campaign's summary
                // would otherwise read "records have been removed" directly above "the queue
                // processor will create jobs shortly" — but leaving the panel blank is worse
                // than a neutral line, so swap the copy rather than dropping it.
                <p className="text-sm text-muted-foreground text-center py-8">
                  {windowNotice
                    ? 'No scheduled sends to display.'
                    : 'No scheduled jobs found yet. The queue processor will create jobs shortly.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate(`/inbox/campaigns/${id}`)}>
              View Campaign Details
            </Button>
            <Button variant="outline" onClick={() => navigate('/inbox?tab=campaigns')}>
              Back to Campaigns
            </Button>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
}
